import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Mail, User, Lock, Loader2, ArrowLeft, ArrowRight, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const CARGO = { porteiro: "Colaborador", zelador: "Zelador", sindico: "Síndico" };

/**
 * Transição do login antigo (código do condomínio + e-mail) para usuário e senha.
 * 1) a pessoa entra como antes  2) escolhe o usuário e cria a senha  3) já entra no sistema.
 */
export default function PrimeiroAcesso({ onVoltar, onPronto }) {
  const { entrarComResposta } = useAuth();
  const [passo, setPasso] = useState(1);
  const [codigo, setCodigo] = useState("");
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState(null); // {token, condominio_nome, pessoas}
  const [pessoaId, setPessoaId] = useState("");
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [senha2, setSenha2] = useState("");
  const [verSenha, setVerSenha] = useState(true);
  const [carregando, setCarregando] = useState(false);

  const escolher = (p) => { setPessoaId(p.id); setLogin(p.sugestao || ""); };

  const passo1 = async (e) => {
    e.preventDefault();
    if (!codigo.trim() || !email.trim()) return;
    setCarregando(true);
    try {
      const { data } = await axios.post(`${API}/auth/primeiro-acesso`, { codigo: codigo.trim(), email: email.trim() });
      setInfo(data);
      if (data.pessoas.length === 1) escolher(data.pessoas[0]);
      setPasso(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível continuar");
    } finally {
      setCarregando(false);
    }
  };

  const passo2 = async (e) => {
    e.preventDefault();
    if (!pessoaId) return toast.error("Escolha quem é você");
    if (login.length < 3) return toast.error("O usuário precisa ter pelo menos 3 letras");
    if (senha.length < 4) return toast.error("A senha precisa ter pelo menos 4 caracteres");
    if (senha !== senha2) return toast.error("As duas senhas não são iguais");
    setCarregando(true);
    try {
      const { data } = await axios.post(`${API}/auth/definir-senha`, { token: info.token, usuario_id: pessoaId, usuario: login, senha });
      entrarComResposta(data);
      onPronto?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar o acesso");
      if (err.response?.status === 401) setPasso(1);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="primeiro-acesso">
      <button type="button" onClick={onVoltar} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Voltar para o login
      </button>

      <div>
        <h2 className="font-heading text-2xl text-slate-900">PRIMEIRO ACESSO</h2>
        <p className="text-sm text-slate-500 mt-1">
          {passo === 1
            ? "Entre como você entrava antes: código do condomínio e e-mail. Depois você cria seu usuário e senha."
            : `${info?.condominio_nome || ""} — agora crie seu usuário e senha. Das próximas vezes você entra só com eles.`}
        </p>
        <div className="flex gap-1.5 mt-3">
          <span className={`h-1.5 flex-1 rounded-full ${passo >= 1 ? "bg-orange-500" : "bg-slate-200"}`} />
          <span className={`h-1.5 flex-1 rounded-full ${passo >= 2 ? "bg-orange-500" : "bg-slate-200"}`} />
        </div>
      </div>

      {passo === 1 ? (
        <form onSubmit={passo1} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Código do condomínio</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/\s/g, ""))}
                placeholder="ex.: DECK23" autoCapitalize="characters" className="!pl-11 h-12 text-base font-mono" data-testid="pa-codigo" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">E-mail cadastrado</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none"
                placeholder="o e-mail que você usava para entrar" className="!pl-11 h-12 text-base" data-testid="pa-email" />
            </div>
          </div>
          <Button type="submit" disabled={carregando} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base" data-testid="pa-continuar">
            {carregando ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Continuar <ArrowRight className="w-5 h-5 ml-2" /></>)}
          </Button>
        </form>
      ) : (
        <form onSubmit={passo2} className="space-y-4">
          {info.pessoas.length > 1 && (
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Quem é você?</Label>
              <div className="grid gap-2">
                {info.pessoas.map((p) => (
                  <button key={p.id} type="button" onClick={() => escolher(p)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-left ${pessoaId === p.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-400"}`}>
                    {pessoaId === p.id ? <CheckCircle2 className="w-5 h-5 text-orange-500" /> : <User className="w-5 h-5 text-slate-400" />}
                    <span className="font-medium text-slate-900">{p.nome}</span>
                    <span className="text-xs text-slate-500 ml-auto">{CARGO[p.cargo] || p.cargo}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {info.pessoas.length === 1 && (
            <p className="text-sm bg-slate-50 rounded-lg p-3">Olá, <b>{info.pessoas[0].nome}</b> ({CARGO[info.pessoas[0].cargo] || info.pessoas[0].cargo})</p>
          )}
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Seu usuário</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input value={login} onChange={(e) => setLogin(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
                autoCapitalize="none" className="!pl-11 h-12 text-base font-mono" data-testid="pa-usuario" />
            </div>
            <p className="text-xs text-slate-500">Já sugerimos um. Pode trocar se quiser. Anote para não esquecer.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Crie uma senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input type={verSenha ? "text" : "password"} value={senha} onChange={(e) => setSenha(e.target.value)}
                placeholder="Mínimo 4 caracteres" autoComplete="new-password" className="!pl-11 !pr-11 h-12 text-base" data-testid="pa-senha" />
              <button type="button" onClick={() => setVerSenha(!verSenha)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {verSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <Input type={verSenha ? "text" : "password"} value={senha2} onChange={(e) => setSenha2(e.target.value)}
              placeholder="Repita a senha" autoComplete="new-password" className="h-12 text-base" data-testid="pa-senha2" />
          </div>
          <Button type="submit" disabled={carregando} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold text-base" data-testid="pa-criar">
            {carregando ? <Loader2 className="w-5 h-5 animate-spin" /> : "Criar acesso e entrar"}
          </Button>
        </form>
      )}
    </div>
  );
}
